import * as Tabs from '@radix-ui/react-tabs';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { HeaderConnectButton } from '@/components/Header/HeaderButtons';
import {
  HistoryPanels,
  useOpenOrdersCount,
  type HistoryView,
} from '@/components/History/HistoryPanels';
import { parseHistoryQuery } from '@/components/History/utils';

import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useWalletPortfolio } from '@/hooks/useWalletPortfolio';

import { recentTransactionSelector, useTransactionStore } from '@/store/transaction';

import { tabCountStyles, tabsStyles } from '@/styles/tabs';
import { fontStyles } from '@/styles/typography';

import { classNames } from '@/utils/classnames';
import type { EthereumAddress, SolanaAddress } from '@/utils/models';
import { parseProfileView, profileRoute, type ProfileView } from '@/utils/profileRoutes';

import { Portfolio } from '.';
import { PortfolioWalletSelector } from './PortfolioWalletSelector';
import type { WalletFilter } from './types';
import { buildConnectedWalletOptions, getWalletTotals } from './utils';

/** Primary tabs on /profile; History opens on its Swaps view. */
const PRIMARY_TABS = [
  { id: 'portfolio', label: 'Portfolio', view: 'portfolio' },
  { id: 'history', label: 'History', view: 'swaps' },
  { id: 'open-orders', label: 'Open orders', view: 'open-orders' },
] as const satisfies { id: string; label: string; view: ProfileView }[];

const HISTORY_SUB_TABS = [
  { label: 'Swaps', view: 'swaps' },
  { label: 'Advanced', view: 'limit-orders' },
] as const satisfies { label: string; view: HistoryView }[];

const containerClass = classNames(
  'bg-special-background-card',
  'rounded-[20px]',
  'desktop:max-w-[1240px]',
  'overflow-x-hidden',
  'mx-auto',
);

const tabsClasses = tabsStyles({ variant: 'card' });
const primaryTabsClasses = tabsStyles({ variant: 'tertiary' });
const primaryTabClass = [
  'rounded-full',
  'px-3.5',
  'gap-2',
  'text-token-gray-700',
  'hover:text-token-gray-900',
  'data-[state=active]:text-token-gray-1600',
];

export type PortfolioTabsProps = {
  /** The selected view, from the URL. */
  view: ProfileView;
};

/**
 * Portfolio, History and Open orders under one destination, sharing the
 * wallet selection.
 */
export const PortfolioTabs = ({ view }: PortfolioTabsProps) => {
  const router = useRouter();
  const { ethereumWallet, solanaWallet, restoringWalletAddresses } = useMatchaWallets();
  const [requestedWallet, setSelectedWallet] = useState<WalletFilter>('all');
  const { setRecentTransaction } = useTransactionStore(recentTransactionSelector);

  // History lives here, so landing here acknowledges the recent-transaction dot.
  useEffect(() => setRecentTransaction(false), [setRecentTransaction]);

  // Keep the tab in sync with the URL so browser back/forward works under
  // shallow pushes.
  const currentView = parseProfileView(router.query.tab) ?? view;
  const currentTab =
    currentView === 'portfolio' || currentView === 'open-orders' ? currentView : 'history';

  // Checksummed, since the transaction store keys pending trades by it.
  const evmAddress = ethereumWallet?.address ?? restoringWalletAddresses?.ethereum;
  // Zerion is queried by lowercase address, like the Portfolio tab does.
  const portfolioEvmAddress = evmAddress?.toLowerCase();
  const solAddress = solanaWallet?.address ?? restoringWalletAddresses?.solana;
  const isConnected = Boolean(evmAddress || solAddress);

  const walletOptions = useMemo(
    () =>
      buildConnectedWalletOptions(
        { address: portfolioEvmAddress, wallet: ethereumWallet },
        { address: solAddress, wallet: solanaWallet },
      ),
    [portfolioEvmAddress, solAddress, ethereumWallet, solanaWallet],
  );

  // A selected wallet that disconnects falls back to every remaining wallet.
  const selectedWallet =
    requestedWallet === 'all' || walletOptions.some(({ id }) => id === requestedWallet)
      ? requestedWallet
      : 'all';

  // Same query key as the portfolio tab's unfiltered fetch, so this is a cache hit.
  const { data: unfilteredPortfolio } = useWalletPortfolio({
    evmAddress: portfolioEvmAddress,
    solanaAddress: solAddress,
    enabled: isConnected,
  });
  const walletTotals = useMemo(() => getWalletTotals(unfilteredPortfolio), [unfilteredPortfolio]);

  const funders = useMemo(
    () => ({
      ethereum: (selectedWallet === 'solana' ? undefined : evmAddress) as
        | EthereumAddress
        | undefined,
      solana: (selectedWallet === 'evm' ? undefined : solAddress) as SolanaAddress | undefined,
    }),
    [selectedWallet, evmAddress, solAddress],
  );

  const openOrdersCount = useOpenOrdersCount(funders);

  // Read on every render so a remounted pane starts from the current URL.
  const historyQuery = parseHistoryQuery(router.query, {
    allowStatus: currentView === 'limit-orders',
  });

  // Switching tabs drops the previous tab's table filters, which do not carry over.
  const pushView = useCallback(
    (nextView: ProfileView) => {
      router.push(profileRoute(nextView), undefined, { shallow: true, scroll: false });
    },
    [router],
  );

  const walletSelection = useMemo(
    () => ({ selected: selectedWallet, onChange: setSelectedWallet }),
    [selectedWallet],
  );

  if (!isConnected) {
    return (
      <div className="bg-special-background-app bg-(image:var(--gradient-special-homepage)) min-h-screen">
        <div className="h-[62px] [body[data-show-banner='true']_&]:h-[102px]" />
        <div className="py-4 px-4">
          <div
            className={classNames(
              containerClass,
              'flex items-center justify-center w-full h-[75vh]',
            )}
          >
            <div className="h-fit text-center">
              <span
                className={fontStyles({ variant: 'LABEL_LG', class: 'flex justify-center mb-2' })}
              >
                Your profile will appear here.
              </span>
              <div
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'flex justify-center mt-2 mb-4 text-token-gray-700',
                })}
              >
                Connect wallet to see your balances, history and open orders.
              </div>
              <span className="flex justify-center">
                <HeaderConnectButton source="trade_history_page" />
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-special-background-app bg-(image:var(--gradient-special-homepage)) min-h-screen">
      <div className="h-[62px] [body[data-show-banner='true']_&]:h-[102px]" />

      <div className="px-4 pt-4 tablet:pt-8">
        <div className="desktop:max-w-[1240px] mx-auto flex flex-wrap items-center gap-3">
          {walletOptions.length > 0 && (
            <PortfolioWalletSelector
              walletOptions={walletOptions}
              selectedWallet={selectedWallet}
              onWalletChange={setSelectedWallet}
              walletTotals={walletTotals}
              isWatchOnly={false}
            />
          )}

          <Tabs.Root
            className="min-w-0 max-w-full"
            value={currentTab}
            onValueChange={(id) => {
              const tab = PRIMARY_TABS.find((primaryTab) => primaryTab.id === id);
              if (tab) pushView(tab.view);
            }}
          >
            <Tabs.List aria-label="profile views" className={primaryTabsClasses.container()}>
              {PRIMARY_TABS.map(({ id, label }) => (
                <Tabs.Trigger
                  key={id}
                  value={id}
                  className={primaryTabsClasses.tab({ class: primaryTabClass })}
                >
                  {label}
                  {id === 'open-orders' && openOrdersCount !== undefined && (
                    <span className={tabCountStyles}>{openOrdersCount}</span>
                  )}
                </Tabs.Trigger>
              ))}
            </Tabs.List>
          </Tabs.Root>
        </div>
      </div>

      {currentView === 'portfolio' && <Portfolio walletSelection={walletSelection} embedded />}

      {currentView !== 'portfolio' && (
        <div className="py-4 px-4 mb-3 tablet:mb-13">
          {/* Open orders has no sub-tab row, so the card supplies its top spacing. */}
          <div className={classNames(containerClass, { 'pt-4.5': currentTab === 'open-orders' })}>
            {currentTab === 'history' && (
              <Tabs.Root
                value={currentView}
                onValueChange={(value) => pushView(value as HistoryView)}
              >
                <div className="no-scrollbar px-7 py-4.5">
                  <Tabs.List aria-label="view transactions" className={tabsClasses.container()}>
                    {HISTORY_SUB_TABS.map(({ label, view: subView }) => (
                      <Tabs.Trigger key={subView} value={subView} className={tabsClasses.tab()}>
                        {label}
                      </Tabs.Trigger>
                    ))}
                  </Tabs.List>
                </div>
              </Tabs.Root>
            )}

            {/* Keyed so each pane starts with fresh pagination and filters. */}
            <HistoryPanels
              key={currentView}
              view={currentView}
              funders={funders}
              {...historyQuery}
              viewQuery={profileRoute(currentView).query}
            />
          </div>
        </div>
      )}
    </div>
  );
};
